import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

const AssistantCtx = createContext(null);
export const useAssistant = () => useContext(AssistantCtx);

export function deriveScreenContext(pathname) {
  let m = matchPath("/trips/:tripId/live", pathname);
  if (m) return { screen: "live", trip_id: m.params.tripId };
  m = matchPath("/trips/:tripId", pathname);
  if (m) return { screen: "trip", trip_id: m.params.tripId };
  if (pathname.startsWith("/insights/budget")) return { screen: "budget" };
  if (pathname.startsWith("/insights/presence")) return { screen: "presence_act60" };
  if (pathname.startsWith("/plan/scenarios")) return { screen: "scenarios" };
  if (pathname.startsWith("/plan/wishlist")) return { screen: "wishlist" };
  if (pathname.startsWith("/plan/search")) return { screen: "search" };
  if (pathname.startsWith("/you")) return { screen: "you_settings" };
  if (pathname.startsWith("/trips")) return { screen: "trips" };
  if (pathname.startsWith("/manual")) return { screen: "paste_email" };
  return { screen: "home" };
}

export default function AssistantProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [panelSessionId, setPanelSessionId] = useState(null);
  const [prefill, setPrefill] = useState("");
  const [contextOverride, setContextOverride] = useState(null);
  const expandRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const overrideRef = useRef(null);
  overrideRef.current = contextOverride;

  const openAssistant = useCallback((opts) => {
    const o = opts && typeof opts === "object" && ("context" in opts || "prefill" in opts) ? opts : null;
    if (o?.context) setContextOverride(o.context);
    if (o?.prefill) setPrefill(o.prefill);
    if (pathRef.current.startsWith("/plan/ai")) {
      document.querySelector('[data-testid="chat-input"]')?.focus();
      return;
    }
    setOpen(true);
  }, []);

  const closeAssistant = useCallback(() => {
    setOpen(false);
    setContextOverride(null);
    setPrefill("");
  }, []);

  const expand = useCallback(() => {
    expandRef.current = panelSessionId;
    setOpen(false);
    navigate("/plan/ai");
  }, [panelSessionId, navigate]);

  const consumeExpandSession = useCallback(() => {
    const s = expandRef.current;
    expandRef.current = null;
    return s;
  }, []);

  const getScreenContext = useCallback(() => overrideRef.current || deriveScreenContext(pathRef.current), []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        openAssistant();
      } else if (e.key === "Escape") {
        closeAssistant();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openAssistant, closeAssistant]);

  return (
    <AssistantCtx.Provider
      value={{
        open, openAssistant, closeAssistant, expand, consumeExpandSession,
        panelSessionId, setPanelSessionId, getScreenContext, prefill,
        screenContext: contextOverride || deriveScreenContext(location.pathname),
      }}
    >
      {children}
    </AssistantCtx.Provider>
  );
}
